import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { getSettings, updateSettings } from '@/lib/api/settings';
import { refreshStore } from '@/lib/store';

interface IgPost {
  id: number;
  image_url: string;
  caption: string;
  shortcode: string;
  likes: number;
  comments: number;
  posted_at: string;
  sort_order: number;
}

interface InstagramStats {
  followers: string;
  posts: string;
  likes: string;
}

const DEFAULT_STATS: InstagramStats = {
  followers: '12.4k',
  posts: '238',
  likes: '48.6k',
};

export default function AdminSocialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<IgPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Stats
  const [stats, setStats] = useState<InstagramStats>(DEFAULT_STATS);
  const [statsSaved, setStatsSaved] = useState(false);

  // Form fields
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formCaption, setFormCaption] = useState('');
  const [formShortcode, setFormShortcode] = useState('');
  const [formLikes, setFormLikes] = useState(0);
  const [formComments, setFormComments] = useState(0);
  const [formPostedAt, setFormPostedAt] = useState('');

  // Auth check
  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('instagram_posts')
        .select('*')
        .order('sort_order', { ascending: true });

      if (data) setPosts(data as IgPost[]);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }

    // Load Instagram display stats from site_settings
    try {
      const s = await getSettings();
      if (s) setStats({ followers: s.ig_followers, posts: s.ig_posts, likes: s.ig_likes });
    } catch { /* ignore */ }
  };

  const saveStats = async (newStats: InstagramStats) => {
    setStats(newStats);
    try {
      await updateSettings({
        ig_followers: newStats.followers,
        ig_posts: newStats.posts,
        ig_likes: newStats.likes,
      });
      await refreshStore();
      window.dispatchEvent(new Event('admin-instagram-stats-updated'));
      setStatsSaved(true);
      setTimeout(() => setStatsSaved(false), 2000);
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormImageUrl('');
    setFormCaption('');
    setFormShortcode('');
    setFormLikes(0);
    setFormComments(0);
    setFormPostedAt('');
  };

  const openAddForm = () => {
    resetForm();
    setFormPostedAt(new Date().toISOString().slice(0, 16));
    setShowForm(true);
  };

  const openEditForm = (post: IgPost) => {
    setEditingId(post.id);
    setFormImageUrl(post.image_url);
    setFormCaption(post.caption);
    setFormShortcode(post.shortcode);
    setFormLikes(post.likes);
    setFormComments(post.comments);
    setFormPostedAt(post.posted_at ? new Date(post.posted_at).toISOString().slice(0, 16) : '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formImageUrl.trim() || !formCaption.trim()) return;
    setSaving(true);
    setSaved(false);

    const payload = {
      image_url: formImageUrl.trim(),
      caption: formCaption.trim(),
      shortcode: formShortcode.trim(),
      likes: formLikes,
      comments: formComments,
      posted_at: formPostedAt ? new Date(formPostedAt).toISOString() : new Date().toISOString(),
    };

    try {
      if (editingId) {
        // Update existing
        await supabase
          .from('instagram_posts')
          .update(payload)
          .eq('id', editingId);
      } else {
        // Insert new — find max sort_order
        const maxSort = posts.reduce((max, p) => Math.max(max, p.sort_order || 0), -1);
        await supabase
          .from('instagram_posts')
          .insert({ ...payload, sort_order: maxSort + 1 });
      }

      await loadData();
      window.dispatchEvent(new Event('admin-social-updated'));
      resetForm();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('adm2.confirm_delete_post'))) return;
    try {
      await supabase.from('instagram_posts').delete().eq('id', id);
      await loadData();
      window.dispatchEvent(new Event('admin-social-updated'));
    } catch {
      // silent
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const a = posts[index - 1];
    const b = posts[index];
    try {
      await supabase.from('instagram_posts').update({ sort_order: index }).eq('id', a.id);
      await supabase.from('instagram_posts').update({ sort_order: index - 1 }).eq('id', b.id);
      await loadData();
      window.dispatchEvent(new Event('admin-social-updated'));
    } catch { /* ignore */ }
  };

  const handleMoveDown = async (index: number) => {
    if (index >= posts.length - 1) return;
    const a = posts[index];
    const b = posts[index + 1];
    try {
      await supabase.from('instagram_posts').update({ sort_order: index + 1 }).eq('id', a.id);
      await supabase.from('instagram_posts').update({ sort_order: index }).eq('id', b.id);
      await loadData();
      window.dispatchEvent(new Event('admin-social-updated'));
    } catch { /* ignore */ }
  };

  const handleResetStats = () => saveStats(DEFAULT_STATS);

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-3 md:py-0 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/admin/dashboard"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer no-underline shrink-0"
            >
              <i className="ri-arrow-left-line text-foreground-600"></i>
            </Link>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-accent-500/20 to-secondary-500/20 shrink-0">
                <i className="ri-instagram-line text-lg text-accent-600"></i>
              </div>
              <div className="min-w-0">
                <h1 className="font-label text-sm font-semibold text-foreground-900">{t('adm2.social_media')}</h1>
                <p className="text-[10px] text-foreground-500 font-body truncate">{t('adm2.posts_count', { count: posts.length })} · @estevaclinic</p>
              </div>
            </div>
          </div>
          <button
            onClick={openAddForm}
            aria-label={t('adm2.new_post')}
            className="px-4 md:px-5 py-1.5 rounded-lg bg-gradient-to-r from-accent-500 to-secondary-500 text-white text-xs font-label hover:from-accent-600 hover:to-secondary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 shrink-0"
          >
            <i className="ri-add-line"></i>
            <span className="hidden sm:inline">{t('adm2.new_post')}</span>
          </button>
        </div>
        {saved && (
          <div className="bg-green-50 border-t border-green-200 text-center py-2">
            <span className="text-xs text-green-700 font-body">{t('adm2.post_saved_live')}</span>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Editor */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-accent-500/20 to-secondary-500/20">
                <i className="ri-bar-chart-box-line text-lg text-accent-600"></i>
              </div>
              <div>
                <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm2.instagram_stats')}</h2>
                <p className="text-[10px] text-foreground-500 font-body">{t('adm2.instagram_stats_hint')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {statsSaved && (
                <span className="text-xs text-green-600 font-body flex items-center gap-1">
                  <i className="ri-check-line"></i> {t('adm2.saved_short')}
                </span>
              )}
              <button onClick={handleResetStats} className="px-3 py-1.5 rounded-lg border border-background-200 text-[10px] text-foreground-500 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                {t('adm2.reset')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-label uppercase tracking-[0.12em] text-foreground-400 mb-1.5">{t('adm2.followers')}</label>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                  <i className="ri-user-follow-line text-accent-500 text-sm"></i>
                </div>
                <input
                  type="text" value={stats.followers}
                  onChange={(e) => saveStats({ ...stats, followers: e.target.value })}
                  placeholder={t('admy.eg_followers')}
                  className="flex-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-label uppercase tracking-[0.12em] text-foreground-400 mb-1.5">{t('adm2.posts')}</label>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                  <i className="ri-image-line text-accent-500 text-sm"></i>
                </div>
                <input
                  type="text" value={stats.posts}
                  onChange={(e) => saveStats({ ...stats, posts: e.target.value })}
                  placeholder={t('admy.eg_posts')}
                  className="flex-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-label uppercase tracking-[0.12em] text-foreground-400 mb-1.5">{t('adm2.likes_total')}</label>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                  <i className="ri-heart-line text-accent-500 text-sm"></i>
                </div>
                <input
                  type="text" value={stats.likes}
                  onChange={(e) => saveStats({ ...stats, likes: e.target.value })}
                  placeholder={t('admy.eg_likes')}
                  className="flex-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Panel */}
        {showForm && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-label text-sm font-semibold text-foreground-900">
                {editingId ? t('adm2.edit_post') : t('adm2.new_instagram_post')}
              </h2>
              <button onClick={resetForm} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                    {t('adm2.image_url')} <span className="text-accent-500 font-semibold">*</span>
                  </label>
                  <input
                    type="text" value={formImageUrl}
                    onChange={(e) => setFormImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                  />
                  {formImageUrl && (
                    <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden bg-background-100">
                      <img src={formImageUrl} alt={t('adm2.preview')} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                    {t('adm2.instagram_shortcode')}
                  </label>
                  <input
                    type="text" value={formShortcode}
                    onChange={(e) => setFormShortcode(e.target.value)}
                    placeholder={t('admy.eg_shortcode')}
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                  />
                  <p className="text-[10px] text-foreground-400 font-body mt-1">
                    {t('adm2.shortcode_hint')} instagram.com/p/<strong>[shortcode]</strong>/
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">{t('adm2.likes')}</label>
                    <input
                      type="number" value={formLikes}
                      onChange={(e) => setFormLikes(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">{t('adm2.comments')}</label>
                    <input
                      type="number" value={formComments}
                      onChange={(e) => setFormComments(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">{t('adm2.published_at')}</label>
                  <input
                    type="datetime-local" value={formPostedAt}
                    onChange={(e) => setFormPostedAt(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                  {t('adm2.caption')} <span className="text-accent-500 font-semibold">*</span>
                </label>
                <textarea
                  value={formCaption}
                  onChange={(e) => setFormCaption(e.target.value)}
                  rows={6}
                  placeholder={t('adm2.caption_ph')}
                  className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body leading-relaxed focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 transition-all resize-y"
                />
                <p className="mt-2 text-[10px] text-foreground-400 font-body">
                  {t('adm2.caption_hint')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100">
              <button
                onClick={resetForm}
                className="px-4 py-2 rounded-lg border border-background-200 text-xs text-foreground-600 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm2.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!formImageUrl.trim() || !formCaption.trim() || saving}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-accent-500 to-secondary-500 text-white text-xs font-label hover:from-accent-600 hover:to-secondary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('adm2.saving')}
                  </>
                ) : (
                  <>
                    <i className={editingId ? 'ri-save-line' : 'ri-add-line'}></i>
                    {editingId ? t('adm2.save_changes') : t('adm2.publish_post')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Posts Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {posts.map((post, index) => (
            <div
              key={post.id}
              className="group relative bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
            >
              <div className="aspect-square bg-background-100">
                {post.image_url ? (
                  <img src={post.image_url} alt={post.caption?.slice(0, 60)} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-instagram-line text-4xl text-accent-300"></i>
                  </div>
                )}
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                <p className="text-white text-xs font-body line-clamp-2 leading-relaxed mb-2">{post.caption}</p>
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center gap-1 text-white/80 text-[10px]">
                    <i className="ri-heart-fill text-rose-400 text-xs"></i>
                    {post.likes >= 1000 ? `${(post.likes / 1000).toFixed(1)}k` : post.likes}
                  </span>
                  <span className="flex items-center gap-1 text-white/80 text-[10px]">
                    <i className="ri-chat-3-line text-xs"></i>
                    {post.comments}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditForm(post)}
                    className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] font-label hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-pencil-line text-[10px]"></i> {t('adm2.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="px-3 py-1 rounded-full bg-red-500/50 backdrop-blur-sm text-white text-[10px] font-label hover:bg-red-500/70 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line text-[10px]"></i> {t('adm2.delete')}
                  </button>
                </div>
              </div>

              {/* Meta badges */}
              <div className="absolute top-2 right-2 flex items-center gap-1.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="w-5 h-5 flex items-center justify-center rounded bg-black/40 backdrop-blur-sm text-white text-[10px] hover:bg-black/60 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t('adm2.move_up')}
                >
                  <i className="ri-arrow-up-s-line"></i>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index >= posts.length - 1}
                  className="w-5 h-5 flex items-center justify-center rounded bg-black/40 backdrop-blur-sm text-white text-[10px] hover:bg-black/60 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t('adm2.move_down')}
                >
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
                  <i className="ri-instagram-line text-white text-xs"></i>
                </div>
              </div>

              {/* Info below */}
              <div className="p-3">
                <p className="text-xs text-foreground-700 font-body line-clamp-2 leading-relaxed">{post.caption}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-foreground-400 font-body">
                  <span className="flex items-center gap-1"><i className="ri-heart-line text-rose-400"></i> {post.likes}</span>
                  <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i> {post.comments}</span>
                  <span className="text-foreground-300">
                    {post.posted_at ? new Date(post.posted_at).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-background-200 animate-pulse"></div>
            ))}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/60">
              <i className="ri-instagram-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="mt-4 font-heading text-lg text-foreground-600">{t('adm2.no_posts')}</h2>
            <p className="mt-1 text-sm text-foreground-500 font-body">{t('adm2.no_posts_hint')}</p>
            <button
              onClick={openAddForm}
              className="mt-4 px-6 py-2 rounded-full bg-gradient-to-r from-accent-500 to-secondary-500 text-white text-xs font-label hover:from-accent-600 hover:to-secondary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('adm2.create_first_post')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}